// src/pages/stream-management-grid/components/ViewDevice.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Typography, Space, Tag, Spin, Modal, theme as antdTheme } from "antd";
import Icon from "../../../components/AppIcon";
import { Button } from "../../../components/AntComponents";
import { getDeviceById } from "@/api/deviceService";
import type { DeviceDetailResponse } from "@/interface/responses/deviceResponse";
import { mediamtxService } from '@/mediaservices/mediamtxService';
import { mediasoupService } from '@/mediaservices/mediasoupService';
import { assignHostAndCoHost } from '@/mediaservices/mediamtxService';
import { MediaMTXUpdateBroadcast } from '@/api/broadcastService';
import { BroadcastStorage } from '@/utils/BroadcastStorage';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useTheme } from '@/contexts/ThemeContext';

// Add timezone plugins
dayjs.extend(timezone);
dayjs.extend(utc);

const USE_MEDIAMTX = String(import.meta.env.VITE_USE_MEDIAMTX).toLowerCase() === "true";

const { Text } = Typography;
const { confirm } = Modal;

/* ---------- Updated Types ---------- */
export type StreamMode = "raisedHand" | "stream";

export interface DeviceData {
  deviceId?: number;
  participantName: string;
  deviceType?: "STB" | "phone";
  deviceName?: string;
  device_name?: string; // Add device_name for display purposes
  location?: string;
  connectionStrength?: string; // Keep existing for backward compatibility
  networkQuality?: string;     // Add network quality from WebSocket
  raisedAt?: Date;
  duration?: string;

  // Media sources for the preview:
  streamUrl?: string;
  mediaStream?: MediaStream | null;
}

/* ---------- Helper functions ---------- */
function getDeviceIcon(dt?: DeviceData["deviceType"]) {
  // you said only STB and phone
  switch (dt) {
    case "STB":
      return "Monitor";
    case "phone":
      return "Smartphone";
    default:
      return "Video";
  }
}

// Updated to use NetworkQuality enum
function getNetworkQualityColor(quality?: string) {
  switch (quality?.toLowerCase()) {
    case "excellent":
      return "text-green-500";
    case "good":
      return "text-blue-500";
    case "fair":
      return "text-yellow-500";
    case "poor":
      return "text-red-500";
    default:
      return "text-muted-foreground";
  }
}

// Helper for device status
function getDeviceStatusInfo(status?: string) {
  const isOnline = status?.toLowerCase() === 'online';
  return {
    label: isOnline ? 'Online' : 'Offline',
    color: isOnline ? 'text-green-500' : 'text-red-500',
    bgColor: isOnline ? 'bg-green-500/10' : 'bg-red-500/10'
  };
}

// Helper for network interface icon
function getNetworkInterfaceIcon(networkInterface?: string) {
  switch (networkInterface?.toLowerCase()) {
    case 'wifi':
      return 'Wifi';
    case 'ethernet':
      return 'Cable';
    case 'cellular':
    case 'sim':
      return 'Signal';
    default:
      return 'Globe';
  }
}

// Helper for connection type display name
function getConnectionTypeDisplayName(networkInterface?: string) {
  if (!networkInterface) return "—";
  
  switch (networkInterface.toLowerCase()) {
    case 'sim':
      return 'Cellular';
    case 'wifi':
      return 'WiFi';
    case 'ethernet':
      return 'Ethernet';
    default:
      return networkInterface.charAt(0).toUpperCase() + networkInterface.slice(1).toLowerCase();
  }
}

function formatTimeAgo(date?: Date) {
  if (!date) return "—";
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

// Helper for network quality bars
function getNetworkQualityBars(quality?: string) {
  switch (quality?.toLowerCase()) {
    case "excellent": return 4;
    case "good": return 3;
    case "fair": return 2;
    case "poor": return 1;
    default: return 0;
  }
}

// Add this helper function near the top with other helper functions
function formatDateToIST(dateString?: string) {
  if (!dateString) return "—";
  return dayjs(dateString).tz('Asia/Kolkata').format('DD-MM-YYYY HH:mm');
}

/* ---------- Component ---------- */
const ViewDevice: React.FC<ViewerDrawerProps> = ({
  open,
  mode,
  data,
  onClose,
  onAccept,
  onReject,
  onMakeHost,
  onMakeCoHost,
}) => {
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState<DeviceDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [localMuted, setLocalMuted] = useState(true);
  const pcRef = useRef<RTCPeerConnection | null>(null); // WHEP pc (MediaMTX only)

  //   Add reconnection tracking
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const [connectionState, setConnectionState] = useState<string>('new');
  const [isConnecting, setIsConnecting] = useState(false); // Add buffering state for ViewDevice
  const { hostId } = mediamtxService.getCurrentRoles();
  const { theme: activeTheme } = useTheme();
  const { token } = antdTheme.useToken();

  const confirmRootClass = useMemo(
    () => `themed-confirm themed-confirm-${activeTheme}`,
    [activeTheme]
  );

  const confirmStyles = useMemo(
    () => ({
      content: {
        backgroundColor: token.colorBgElevated,
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorder}`,
        boxShadow:
          activeTheme === 'dark'
            ? '0 16px 48px rgba(0, 0, 0, 0.55)'
            : '0 16px 32px rgba(15, 23, 42, 0.18)',
      },
      body: {
        color: token.colorText,
      },
    }),
    [activeTheme, token.colorBgElevated, token.borderRadiusLG, token.colorBorder, token.colorText]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchDevice = async () => {
      if (!open || data?.deviceId == null) { // null/undefined check (0 allowed)
        setDevice(null);
        setError(null);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await getDeviceById(data.deviceId);
        if (!cancelled) setDevice(res);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load device");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDevice();
    return () => { cancelled = true; };
  }, [open, data?.deviceId]);


  // Play WHEP (MediaMTX) using API's URL (fallback to prop if provided)
  useEffect(() => {
    if (!USE_MEDIAMTX) return;
    const v = videoRef.current;
    
    if (!open || !v || !data?.deviceName) {
      return;
    }

    //   Get WHEP URL from MediaMTX service
    const streamSummaries = mediamtxService.getStreamSummaries();
    const streamEntry = streamSummaries.find(s => 
      s.id === data.deviceName || 
      s.meta?.deviceSerialNumber === data.deviceName ||
      s.meta?.streamId === data.deviceName
    );
    
    const whepUrl = streamEntry?.whepUrl;
    
    if (!whepUrl) {
      console.log('ViewDevice WHEP: No WHEP URL found in WebSocket data for device:', data.deviceName);
      console.log('Available streams:', streamSummaries.map(s => ({ 
        id: s.id, 
        deviceSerial: s.meta?.deviceSerialNumber,
        streamId: s.meta?.streamId 
      })));
      return;
    }

    console.log(`[ViewDevice ${data.deviceName}] Starting WHEP connection (trigger: ${reconnectTrigger})`);

    let pc: RTCPeerConnection | null = null;
    let aborted = false;

    // Set connecting state when starting WHEP connection
    setIsConnecting(true);

    //   Clear existing video and reset state
    if (v.srcObject) {
      (v.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
      v.srcObject = null;
    }
    setConnectionState('connecting');

    v.autoplay = true;
    v.muted = localMuted;
    v.playsInline = true;

    // helper: wait for ICE gathering to "complete" or time out quickly
    const waitForIceGatheringComplete = (peer: RTCPeerConnection) =>
      new Promise<void>((resolve) => {
        if (peer.iceGatheringState === 'complete') return resolve();
        const onChange = () => {
          if (peer.iceGatheringState === 'complete') {
            peer.removeEventListener('icegatheringstatechange', onChange);
            resolve();
          }
        };
        peer.addEventListener('icegatheringstatechange', onChange);
        setTimeout(() => {
          peer.removeEventListener('icegatheringstatechange', onChange);
          resolve();
        }, 1000);
      });

    const startWhepWithRetry = async (maxAttempts = 5) => {
      let attempt = 0;
      let lastError: any = null;

      while (!aborted && attempt < maxAttempts) {
        attempt += 1;

        try {
          // fresh PC each attempt
          pc = new RTCPeerConnection();

          // Monitor connection state changes
          pc.onconnectionstatechange = () => {
            const state = pc?.connectionState;
            setConnectionState(state || 'unknown');
            
            if (state === 'connected') {
              setIsConnecting(false); // Stop showing buffering when connected
            }

            //   Only trigger reconnect if we're still open and have the URL
            if (state === 'failed' || state === 'disconnected') {
              setIsConnecting(false);
              setTimeout(() => {
                if (whepUrl && open && data?.deviceName) {
                  console.log(`[ViewDevice ${data.deviceName}] Triggering reconnect due to connection failure`);
                  setReconnectTrigger(prev => prev + 1);
                }
              }, 2000);
            }
          };

          // Receive-only
          pc.addTransceiver('video', { direction: 'recvonly' });
          pc.addTransceiver('audio', { direction: 'recvonly' });

          // Attach remote stream to <video>
          pc.ontrack = (e) => {
            if (aborted) return;
            if (v.srcObject !== e.streams[0]) {
              v.srcObject = e.streams[0];
              setConnectionState('connected');
              setIsConnecting(false); // Stop showing buffering when video arrives
            }
          };

          // Build SDP offer
          const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: true });
          await pc.setLocalDescription(offer);
          await waitForIceGatheringComplete(pc);

          const localDesc = pc.localDescription;
          if (!localDesc?.sdp) throw new Error('No local SDP');

          // POST to WHEP
          const res = await fetch(whepUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/sdp',
              'Accept': 'application/sdp, text/plain',
            },
            body: localDesc.sdp,
          });

          if (!res.ok) {
            throw new Error(`WHEP POST failed: ${res.status} ${res.statusText}`);
          }

          const answerSdp = await res.text();
          if (aborted) return;

          await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

          // Try to play
          try { 
            await v.play();
          } catch {}

          // Success - break the retry loop
          return;
          
        } catch (err) {
          lastError = err;
          console.warn(`[ViewDevice WHEP ${data.deviceName}] attempt ${attempt} failed`, err);
          try { pc?.getReceivers().forEach(r => r.track?.stop?.()); pc?.close(); } catch {}
          pc = null;

          // Exponential backoff
          const delay = 300 + attempt * 250 + Math.floor(Math.random() * 200);
          await new Promise(r => setTimeout(r, delay));
        }
      }

      console.error(`[ViewDevice WHEP ${data.deviceName}] all attempts failed`, lastError);
      setConnectionState('failed');
      setIsConnecting(false); // Stop showing buffering on failure
    };

    startWhepWithRetry();

    // Cleanup
    return () => {
      aborted = true;
      setIsConnecting(false); // Stop showing buffering on cleanup
      try {
        if (pc) {
          pc.getReceivers().forEach((r) => r.track && r.track.stop && r.track.stop());
          pc.close();
        }
      } catch {}
      pc = null;
      if (v && v.srcObject) {
        (v.srcObject as MediaStream).getTracks().forEach((t) => t.stop());
        v.srcObject = null;
      }
    };
  }, [open, data?.deviceName, localMuted, reconnectTrigger]); //   Include reconnectTrigger

  /* ---------- Media source 2: mediasoup (bind MediaStream) ---------- */
  useEffect(() => {
    if (USE_MEDIAMTX) return; // MediaMTX handled above
    const v = videoRef.current;
    const ms = data?.mediaStream || null;
    if (!open || !v || !ms) return;

    v.autoplay = true;
    v.playsInline = true;
    v.muted = localMuted;
    v.srcObject = ms;
    v.play().catch(() => {});

    return () => {
      if (videoRef.current && videoRef.current.srcObject === ms) {
        videoRef.current.srcObject = null; // do not stop tracks; mediasoup owner handles lifecycle
      }
    };
  }, [open, data?.mediaStream, localMuted]);

  // Keep mute flag in sync
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = localMuted;
    if (!localMuted) v.play().catch(() => {});
  }, [localMuted, open]);

  /* ---------- Updated role change handlers ---------- */
  const handleMakeHost = async () => {
    if (!data) return;

    try {
      if (USE_MEDIAMTX) {
        const deviceSerial = String(data.deviceName || data.participantName);
        const { coHostId } = mediamtxService.getCurrentRoles();

        // Update local roles first
        await assignHostAndCoHost(deviceSerial, coHostId);

        // --- Update broadcast if active ---
        const broadcastStatus = BroadcastStorage.getBroadcastState();
        const devices = BroadcastStorage.getSelectedDevices();
        const streamId = BroadcastStorage.getStreamId();
        
        if (broadcastStatus === 'live' &&devices.length && streamId) {

          const payload = {
            streamName: BroadcastStorage.getBroadcastName() || 'Updated Broadcast',
            devices: devices.map(s => ({
              deviceSerial: s,
              isHost: s === deviceSerial,
              isCoHost: s === coHostId,
              action: 'update' as const,
            })),
            newHostDeviceSerial: deviceSerial,
          };
          
          await MediaMTXUpdateBroadcast(streamId, payload);
        }
      } else {
        const deviceId = String(data.deviceName || data.participantName);
        mediasoupService.setHost(deviceId);
      }

      // ✅ Close the drawer after successful operation
      onClose();
      
    } catch (error) {
      console.error('❌ Failed to make host:', error);
    }
  };

  const handleMakeCoHost = async () => {
    if (!data) return;

    try {
      if (USE_MEDIAMTX) {
        const deviceSerial = String(data.deviceName || data.participantName);
        const { hostId } = mediamtxService.getCurrentRoles();

        // Update local roles first
        await assignHostAndCoHost(hostId, deviceSerial);

        // --- Update broadcast if active ---
        const devices = BroadcastStorage.getSelectedDevices();
        const streamId = BroadcastStorage.getStreamId();
        
        if (devices.length && streamId) {

          const payload = {
            streamName: BroadcastStorage.getBroadcastName() || 'Updated Broadcast',
            devices: devices.map(s => ({
              deviceSerial: s,
              isHost: s === hostId,
              isCoHost: s === deviceSerial,
              action: 'update' as const,
            })),
            newHostDeviceSerial: hostId || deviceSerial,
          };
          await MediaMTXUpdateBroadcast(streamId, payload);
        }
      } else {
        const deviceId = String(data.deviceName || data.participantName);
        mediasoupService.setCoHost(deviceId);
      }

      // ✅ Close the drawer after successful operation
      onClose();
      
    } catch (error) {
      console.error('❌ Failed to make co-host:', error);
      // Don't close on error - let user see the error and potentially retry
    }
  };

  /* ---------- Updated confirmations for role changes ---------- */
  const label = data?.device_name || data?.deviceName || "this device";
  const askMakeHost = () =>
    confirm({
      title: (
        <span style={{ color: token.colorText }}>{`Make ${label} the Host?`}</span>
      ),
      content: (
        <span style={{ color: token.colorTextSecondary }}>
          This will update the user as the Host.
        </span>
      ),
      okText: "Yes, make Host",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await handleMakeHost();
        } catch (error) {
          console.error('Failed to make host:', error);
          return Promise.reject(error);
        }
      },
      rootClassName: confirmRootClass,
      styles: confirmStyles,
      okButtonProps: { type: 'primary' },
      cancelButtonProps: {
        style: {
          color: token.colorTextSecondary,
          borderColor: token.colorBorder,
          background: activeTheme === 'dark' ? token.colorBgContainer : undefined,
        },
      },
    });
    
  const askMakeCoHost = () =>
    confirm({
      title: (
        <span style={{ color: token.colorText }}>{`Make ${label} the Co-Host?`}</span>
      ),
      content: (
        <span style={{ color: token.colorTextSecondary }}>
          This will move the device into the Co-Host slot.
        </span>
      ),
      okText: "Yes, make Co-Host",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await handleMakeCoHost();
        } catch (error) {
          console.error('Failed to make co-host:', error);
          // Return rejected promise to keep modal open on error
          return Promise.reject(error);
        }
      },
      rootClassName: confirmRootClass,
      styles: confirmStyles,
      okButtonProps: { type: 'primary' },
      cancelButtonProps: {
        style: {
          color: token.colorTextSecondary,
          borderColor: token.colorBorder,
          background: activeTheme === 'dark' ? token.colorBgContainer : undefined,
        },
      },
    });

  const title =
    mode === "raisedHand"
      ? `Device : ${data?.device_name ?? "-"}`
      : `Device : ${data?.device_name ?? "-"}`;

  // ✅ Check if device is online
  const isDeviceOnline = device?.latest_heartbeat?.status?.toLowerCase() === 'online';

  const footer =
    mode === "raisedHand" ? (
      <Space>
        <Button onClick={onReject} iconName="X">
          Reject
        </Button>
        <Button type="primary" onClick={onAccept} iconName="Check">
          Accept
        </Button>
      </Space>
    ) : (
      <Space>
        {/* ✅ Only show Make Host button if device is online */}
        {isDeviceOnline && (
          <Button type="primary" onClick={askMakeHost} iconName="Crown">
            Make Host
          </Button>
        )}
        {/* ✅ Only show Make Co-Host button if device is online AND there's a host */}
        {isDeviceOnline && (
          <Button
            type="primary"
            onClick={askMakeCoHost}
            iconName="UserCheck"
            disabled={!hostId} // Disable if no host
          >
            Make Co-Host
          </Button>
        )}
      </Space>
    );

  // Label STB/phone for the details section
  const friendlyDeviceType = useMemo(() => {
    const raw = device?.device_type; // API field: "STB" | "Mobile" | ...
    if (!raw) return "—";
    if (raw === "STB") return "STB";
    if (raw.toLowerCase() === "mobile" || raw.toLowerCase() === "phone") return "Mobile"; // ✅ Change to "Mobile" for consistency
    return raw;
  }, [device?.device_type]);

  // Whether we have a playable media source right now
  const hasMedia = useMemo(() => {
    if (USE_MEDIAMTX) {
      //   Check if we have the device in WebSocket data
      const streamSummaries = mediamtxService.getStreamSummaries();
      const streamEntry = streamSummaries.find(s => 
        s.id === data?.deviceName || 
        s.meta?.deviceSerialNumber === data?.deviceName ||
        s.meta?.streamId === data?.deviceName
      );
      return !!streamEntry?.whepUrl;
    } else {
      return !!data?.mediaStream;
    }
  }, [data?.deviceName, data?.mediaStream]);

  //   Add the missing reconnection listeners in ViewDevice
  useEffect(() => {
    if (!USE_MEDIAMTX || !open || !data?.deviceName) return;

    const handleStreamUpdate = (updateData: any) => {
      if (updateData.meta?.deviceSerialNumber === data.deviceName ||
          updateData.id === data.deviceName) {
            
        setReconnectTrigger(prev => prev + 1);
      }
    };

    const handleStreamRemoved = (removeData: any) => {
      if (removeData.meta?.deviceSerialNumber === data.deviceName ||
          removeData.id === data.deviceName) {
        console.log(`[ViewDevice ${data.deviceName}] Device disconnected`);
        setConnectionState('disconnected');
      }
    };

    mediamtxService.on('streamUpdate', handleStreamUpdate);
    mediamtxService.on('streamRemoved', handleStreamRemoved);
    
    return () => {
      mediamtxService.off('streamUpdate', handleStreamUpdate);
      mediamtxService.off('streamRemoved', handleStreamRemoved);
    };
  }, [open, data?.deviceName]);

  // Get device status info
  const deviceStatus = useMemo(() => {
    return getDeviceStatusInfo(device?.latest_heartbeat?.status);
  }, [device?.latest_heartbeat?.status]);

  // Get network info
  const networkInfo = useMemo(() => {
    const heartbeat = device?.latest_heartbeat;
    return {
      interface: heartbeat?.network_interface,
      quality: heartbeat?.network_quality,
      wifiConnected: heartbeat?.wifi_connected,
      ethernetConnected: heartbeat?.ethernet_connected,
      simConnected: heartbeat?.sim_data_connected,
    };
  }, [device?.latest_heartbeat]);

  // Add state for tracking mute/video states
  const [remoteMicOff, setRemoteMicOff] = useState(false);
  const [deviceSelfMuted, setDeviceSelfMuted] = useState(false);
  const [remoteVideoOff, setRemoteVideoOff] = useState(false);
  const [deviceSelfVideoPaused, setDeviceSelfVideoPaused] = useState(false);

  // Listen for mute/video state updates
  useEffect(() => {
    if (!USE_MEDIAMTX || !open || !data?.deviceName) return;

    const onMuteUpdate = (updateData: { id: string; muteState: any }) => {
      if (updateData.id === data.deviceName) {
        setRemoteMicOff(updateData.muteState?.adminMuted ?? false);
        setDeviceSelfMuted(updateData.muteState?.selfMuted ?? false);
      }
    };

    const onVideoPauseUpdate = (updateData: { id: string; videoPauseState: any }) => {
      if (updateData.id === data.deviceName) {
        setRemoteVideoOff(updateData.videoPauseState?.adminPaused ?? false);
        setDeviceSelfVideoPaused(updateData.videoPauseState?.selfPaused ?? false);
      }
    };

    mediamtxService.on('mute', onMuteUpdate);
    mediamtxService.on('videoPauseState', onVideoPauseUpdate);
    
    return () => {
      mediamtxService.off('mute', onMuteUpdate);
      mediamtxService.off('videoPauseState', onVideoPauseUpdate);
    };
  }, [open, data?.deviceName]);

  // Add this new useEffect AFTER your existing mute/video state listeners:
  useEffect(() => {
    if (!USE_MEDIAMTX || !open || !data?.deviceName) return;

    // Get current mute/video states from mediamtxService when drawer opens
    const getCurrentStates = () => {
      const streamSummaries = mediamtxService.getStreamSummaries();
      const deviceStream = streamSummaries.find(s => 
        s.id === data.deviceName || 
        s.meta?.deviceSerialNumber === data.deviceName ||
        s.meta?.streamId === data.deviceName
      );

      if (deviceStream?.meta) {
        // Set current mute state
        const muteState = deviceStream.meta.muteState || {};
        setRemoteMicOff(muteState.adminMuted ?? false);
        setDeviceSelfMuted(muteState.selfMuted ?? false);

        // Set current video pause state  
        const videoPauseState = deviceStream.meta.videoPauseState || {};
        setRemoteVideoOff(videoPauseState.adminPaused ?? false);
        setDeviceSelfVideoPaused(videoPauseState.selfPaused ?? false);
      }
    };

    // Sync states immediately when drawer opens
    getCurrentStates();

    // Also sync when stream summaries update
    const onStreamStatusUpdate = () => {
      getCurrentStates();
    };

    mediamtxService.on('streamStatusUpdate', onStreamStatusUpdate);
    
    return () => {
      mediamtxService.off('streamStatusUpdate', onStreamStatusUpdate);
    };
  }, [open, data?.deviceName]);

  return (
    <Drawer
      title={title}
      placement="right"
      width={460}
      onClose={onClose}
      open={open}
      destroyOnClose
      extra={footer}
    >
      {!data ? null : (
        <div className="space-y-3">
          {/* Video area */}
          <div className="relative aspect-video bg-muted border border-border rounded-lg overflow-hidden">
            {hasMedia ? (
              <>
                <video ref={videoRef} className="w-full h-full object-contain" />
                
                {/* Buffering/Connecting Overlay */}
                {isConnecting && (
                  <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center space-y-3">
                    <div className="bg-slate-800 bg-opacity-90 rounded-full p-4">
                      <Icon name="Loader2" size={40} className="text-white animate-spin" />
                    </div>
                    <span className="text-lg font-medium text-white">Connecting...</span>
                    <span className="text-xs text-slate-300">Establishing video stream connection</span>
                  </div>
                )}
                
                {/* Video Paused Overlay */}
                {(remoteVideoOff || deviceSelfVideoPaused) && !isConnecting && (
                  <div className="absolute inset-0 bg-black bg-opacity-70 flex flex-col items-center justify-center space-y-3">
                    <div className="bg-slate-800 bg-opacity-90 rounded-full p-4">
                      <Icon name="Pause" size={40} className="text-white" />
                    </div>
                    <span className="text-lg font-medium text-white">
                      {remoteVideoOff ? 'Video Paused by Operator' : 'Video Paused by Device'}
                    </span>
                    <span className="text-xs text-slate-300">
                      {remoteVideoOff ? 'Stream video has been paused by the operator' : 'Device has paused their video stream'}
                    </span>
                  </div>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
                No stream
              </div>
            )}
          </div>

          {/* Details */}
          {loading ? (
            <div className="py-6 flex items-center justify-center">
              <Spin />
            </div>
          ) : error ? (
            <div className="p-3 bg-error/10 border border-error/20 rounded-md text-sm">{error}</div>
          ) : (
            <div className="grid grid-cols-2 gap-2">

              <div>
                <Text type="secondary" className="text-xs">
                  Device Serial Number
                </Text>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-foreground">
                    {data?.participantName || "—"}
                  </span>
                </div>
              </div>

              <div>
                <Text type="secondary" className="text-xs">
                  Device Group
                </Text>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-foreground">
                    {device?.groups && device.groups.length > 0 
                      ? device.groups.map(group => group.name).join(', ')
                      :"—"
                    }
                  </span>
                </div>
              </div>

              <div>
                <Text type="secondary" className="text-xs">
                  Device Type
                </Text>
                <div className="text-sm text-foreground">{friendlyDeviceType}</div>
              </div>

              {/* Only show firmware version if NOT Mobile */}
              {friendlyDeviceType !== "Mobile" && (
                <div>
                  <Text type="secondary" className="text-xs">
                    Firmware Version
                  </Text>
                  <div className="text-sm text-foreground">{device?.firmware_version ?? "—"}</div>
                </div>
              )}

              <div>
                <Text type="secondary" className="text-xs">
                  License
                </Text>
                <div className="text-sm text-foreground">
                  {device?.license?.license_key ?? device?.license_id ?? "—"}
                </div>
              </div>

              {friendlyDeviceType !== "STB" && (
                <div>
                  <Text type="secondary" className="text-xs">
                    User Email
                  </Text>
                  <div className="text-sm text-foreground">{device?.user?.email ?? "—"}</div>
                </div>
              )}

              {friendlyDeviceType !== "STB" && (
                <div>
                  <Text type="secondary" className="text-xs">
                    Phone Number
                  </Text>
                  <div className="text-sm text-foreground">{device?.user?.phone_number ?? "—"}</div>
                </div>
              )}

              {/* Only show location if NOT Mobile */}
              {friendlyDeviceType !== "Mobile" && (
                <div>
                  <Text type="secondary" className="text-xs">
                    Location
                  </Text>
                  <div className="text-sm text-foreground">{device?.location ?? data.location ?? "—"}</div>
                </div>
              )}

              <div>
                <Text type="secondary" className="text-xs">
                  {mode === "raisedHand" ? "Raised" : "Last Heartbeat"}
                </Text>
                <div className="text-sm text-foreground">
                  {mode === "raisedHand"
                    ? formatTimeAgo(data.raisedAt)
                    : formatDateToIST(device?.last_heartbeat) //   Use IST formatter
                  }
                </div>
              </div>

              {isDeviceOnline && (
                <>
                  {/* Connection Type */}
                  <div>
                    <Text type="secondary" className="text-xs">
                      Connection Type
                    </Text>
                    <div className="flex items-center gap-2">
                      <Icon 
                        name={getNetworkInterfaceIcon(networkInfo.interface)} 
                        size={14} 
                        className="text-muted-foreground" 
                      />
                      <span className="text-sm text-foreground">
                        {getConnectionTypeDisplayName(networkInfo.interface)}
                      </span>
                    </div>
                  </div>

                  {/* Connection Strength */}
                  {/* <div>
                    <Text type="secondary" className="text-xs">
                      Connection Strength
                    </Text>
                    <div className="flex items-center gap-2">
                      <Tag className={getNetworkQualityColor(networkInfo.quality)}>
                        {networkInfo.quality 
                          ? networkInfo.quality.charAt(0).toUpperCase() + networkInfo.quality.slice(1).toLowerCase()
                          : "—"
                        }
                      </Tag>
                      <div className="flex space-x-0.5">
                        {[1, 2, 3, 4].map((bar) => (
                          <div
                            key={bar}
                            className={`w-0.5 h-2 rounded-sm ${
                              bar <= getNetworkQualityBars(networkInfo.quality)
                                ? getNetworkQualityColor(networkInfo.quality)?.replace("text-", "bg-")
                                : "bg-muted"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div> */}

                  {/* Video Quality */}
                  {/* <div>
                    <Text type="secondary" className="text-xs">
                      Video Quality
                    </Text>
                    <div className="flex items-center gap-1">
                      <Icon name="Video" size={12} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">
                        {device?.latest_heartbeat?.video_quality ?? "—"}
                      </span>
                    </div>
                  </div> */}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
};

export default ViewDevice;
